import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { api, getApiEndpointName } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import EntityMemberSchema, { EntityMemberJSON } from "_shared/entity-members/entity-member-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import CreateUserEntityMemberInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/create-user-entity-member-invitation-api-command";
import UserEntityMemberInvitation from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { LegalEntityNotFoundError } from "legal-entities/legal-entity";
import { HttpStatusCode } from "axios";
import z from "zod";

type LegalEntityMembersArgs = {
  legalEntityId: string;
};

type UserEntityMemberInvitationArgs = {
  entityId: Guid;
  invitation: UserEntityMemberInvitation;
};

export const entityMemberApi = api.injectEndpoints({
  endpoints: (build) => ({
    createEntityMemberInvitation: build.mutation<Guid, UserEntityMemberInvitationArgs>({
      query: ({ entityId, invitation }) => ({
        url: `legal-entities/${entityId.value}/user-entity-member-invitations`,
        method: "POST",
        body: JSON.stringify(new CreateUserEntityMemberInvitationAPIRequest(invitation)),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: GuidSchema.GuidJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: string) => new Guid(GuidSchema.deserialize(response)),
      transformErrorResponse: (response: FetchBaseQueryError, meta) =>
        toEntityMemberApiError(response, meta),
    }),

    getEntityMembersByEntityId: build.query<EntityMemberJSON[], LegalEntityMembersArgs>({
      query: ({ legalEntityId }) => `legal-entities/${legalEntityId}/members/`,
      rawResponseSchema: z.array(EntityMemberSchema.EntityMemberJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformErrorResponse: (response: FetchBaseQueryError, meta, { legalEntityId }) => toEntityMemberApiError(response, meta, legalEntityId),
      providesTags: (_result, _error, { legalEntityId }) => [{ type: "EntityMember" as const, id: legalEntityId }],
    }),

  }),
});

export const {
  useCreateEntityMemberInvitationMutation,
  useGetEntityMembersByEntityIdQuery,
} = entityMemberApi;

export class InvalidInvitationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidInvitationError";
  }
}

export class EntityMemberAPIError extends Error {
  constructor(methodName: string | undefined, public readonly error: unknown) {
    const endpointName = methodName ? `.${methodName}` : "";
    super(`Error in entityMemberApi${endpointName}: ${getErrorMessageFromUnknown(error) ?? "An error occurred"}`);
  }
}

export class UnauthorizedEntityMemberAccessError extends Error {
  constructor(entityId: Guid) {
    super(`Unauthorized access to legal entity members for entity ${entityId}`);
  }
}

function toEntityMemberApiError(response: FetchBaseQueryError, meta?: unknown, legalEntityId?: string): Error {
  const status = getApiErrorStatus(response);
  const entityId = legalEntityId ? Guid.fromJSON(legalEntityId) : undefined;

  if (status === HttpStatusCode.BadRequest) {
    return new InvalidInvitationError(getErrorMessageFromUnknown(response) ?? "Request failed with status code 400");
  }
  if (status === HttpStatusCode.Forbidden && entityId) {
    return new UnauthorizedEntityMemberAccessError(entityId);
  }
  if (status === HttpStatusCode.NotFound && entityId) {
    return new LegalEntityNotFoundError(entityId);
  }

  return new EntityMemberAPIError(getApiEndpointName(meta), response);
}
